import { IButtonRoleVisibility } from "./ButtonRoleVisibility.types";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { api } from "../../api/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { ERROR_MESSAGES } from "../../constants/enums";
import { AppDispatch, RootState } from "../store";

export const fetchButtonRoleVisibilityList = createAsyncThunk<
  {
    data: IButtonRoleVisibility[];
    totalRecords: number;
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: APIError }
>(
  "buttonRoleVisibility/fetchButtonRoleVisibilityList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `/general/get-button-role-visibility?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/general/get-button-role-visibility?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: IButtonRoleVisibility[] = res.data.data;
      const totalRecords: number = res.data.totalRecords ?? 0;
      return {
        data,
        totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchButtonVisibilityIfNeeded =
  (code: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (state.buttonRoleVisibility.runtimeRefData[code]) return;

    dispatch(fetchButtonVisibilityRecord(code));
  };

export const fetchButtonVisibilityRecord = createAsyncThunk<
  {
    code: string;
    record: IButtonRoleVisibility | null;
  },
  string,
  { rejectValue: APIError }
>(
  "buttonRoleVisibility/fetchButtonVisibilityRecord",
  async (code, { rejectWithValue }) => {
    try {
      const [button_name, module_name, sub_module] = code.split("::");
      const res = await api.get(
        `/general/get-button-role-visibility?button_name=${button_name}&module_name=${module_name}&sub_module=${sub_module}`,
      );
      const data: IButtonRoleVisibility[] = res.data.data;
      return {
        code,
        record: data.length > 0 ? data[0] : null,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchButtonVisibilityByCodeIfNeeded =
  (buttonRoleCode: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (state.buttonRoleVisibility.runtimeRefData[buttonRoleCode]) return;

    dispatch(fetchButtonVisibilityRecordByCode(buttonRoleCode));
  };

export const fetchButtonVisibilityRecordByCode = createAsyncThunk<
  {
    code: string;
    record: IButtonRoleVisibility | null;
  },
  string,
  { rejectValue: APIError }
>(
  "buttonRoleVisibility/fetchButtonVisibilityRecordByCode",
  async (buttonRoleCode, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/general/get-button-role-visibility?button_role_code=${buttonRoleCode}`,
      );
      const data: IButtonRoleVisibility[] = res.data.data;
      return {
        code: buttonRoleCode,
        record: data.length > 0 ? data[0] : null,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertButtonRoleVisibility = createAsyncThunk<
  {},
  { data: IButtonRoleVisibility },
  { rejectValue: APIError }
>(
  "buttonRoleVisibility/upsertButtonRoleVisibility",
  async ({ data }, { rejectWithValue }) => {
    try {
      await api.post("/general/button-role-visibility", {
        ...data,
      });
      return {};
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
